// PITH cart add-ons. One-click "Add" for products in the `cart-add-ons`
// collection, shown in the cart drawer (see snippets/cart-add-ons.liquid).
// Adds the variant, asks Shopify to re-render the cart section, then dispatches
// CartAddEvent so component-cart-items.js refreshes the drawer (which re-renders
// this element without the just-added product) and cart-drawer.js reacts.
//
// The click handler is delegated from `document` and bound once, so it keeps
// working after the header section is morphed (which replaces the whole
// <cart-add-ons> element). The add URL is derived from Shopify.routes rather
// than the theme's `Theme` global, which is a block-scoped `const` and is not
// reliably visible from a module in every load order.
import { CartAddEvent } from '@theme/events';

const CART_ADD_URL = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart/add.js`.replace(
  /\/{2,}/g,
  '/'
);

if (!window.__pithCartAddOns) {
  window.__pithCartAddOns = true;

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('cart-add-ons [data-cart-addon-add]');
    if (button instanceof HTMLButtonElement && button.dataset.busy !== 'true') addAddon(button);
  });
}

/**
 * @param {HTMLButtonElement} button
 */
async function addAddon(button) {
  const variantId = button.dataset.variantId;
  if (!variantId) return;

  button.dataset.busy = 'true';
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  const sectionIds = [
    ...new Set(
      [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean)
    ),
  ];

  const body = new FormData();
  body.append('id', variantId);
  body.append('quantity', '1');
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  try {
    const response = await fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.status) {
      // Shopify rejected it (e.g. went out of stock) — leave the button for the
      // `finally` block to re-enable so the shopper can retry.
      return;
    }

    document.dispatchEvent(
      new CartAddEvent({}, 'cart-add-ons', {
        source: 'cart-add-ons',
        itemCount: 1,
        productId: button.dataset.productId,
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error('cart-add-ons:', error);
  } finally {
    // On success the drawer re-render removes this button; this is a safety net
    // for the cases where it doesn't (rejected add, section failed to render),
    // so the shopper is never left with a dead "Add" button.
    setTimeout(() => {
      if (!button.isConnected) return;
      button.dataset.busy = 'false';
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }, 1500);
  }
}
